import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Params, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Committees, db_GetCommitteeRow } from '../../../api';
import Breadcrumbs, { BreadcrumbItem } from '../../../components/govuk/Breadcrumbs';
import Button from '../../../components/govuk/Button';
import DateInput from '../../../components/govuk/DateInput';
import ErrorSummary from '../../../components/govuk/ErrorSummary';
import Fieldset, { Legend } from '../../../components/govuk/Fieldset';
import { GridColumn, GridRow } from '../../../components/govuk/Grid';
import { formatDateAsUTC, parseDateString } from '../../../utils/DataValidation';
import LoadingPage from '../../LoadingPage';
import { getCommitteeQuery } from '../committeeQuery';

interface CommitteeDateProps {
  fieldName: string;
  label: string;
  maxDateFieldName?: string;
  maxDateLabel?: string;
  minDateFieldName?: string;
  minDateLabel?: string;
}

interface CommitteeDateFormProps extends CommitteeDateProps {
  committeeData: db_GetCommitteeRow;
}

function CommitteeDateForm({
  committeeData,
  fieldName,
  label,
  maxDateFieldName,
  maxDateLabel,
  minDateFieldName,
  minDateLabel,
}: CommitteeDateFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const committeeID = committeeData.committee_id ?? 0;
  const date = committeeData?.[fieldName as keyof typeof committeeData]?.toString().slice(0, -1);

  const FormSchema = z
    .object({
      date: z.optional(z.date()),
    })
    .refine(
      (data) =>
        !maxDateFieldName ||
        (data.date &&
          data.date.getTime() <
            parseDateString(committeeData[(maxDateFieldName + '_formatted') as keyof typeof committeeData]).getTime()),
      {
        message: `${label} must be before ${maxDateLabel} (${committeeData[(maxDateFieldName + '_formatted') as keyof typeof committeeData]})`,
        path: ['date'],
      },
    )
    .refine(
      (data) =>
        !minDateFieldName ||
        (data.date &&
          data.date.getTime() >
            parseDateString(committeeData[(minDateFieldName + '_formatted') as keyof typeof committeeData]).getTime()),
      {
        message: `${label} must be after ${minDateLabel} (${committeeData[(minDateFieldName + '_formatted') as keyof typeof committeeData]})`,
        path: ['date'],
      },
    );
  type FormData = z.infer<typeof FormSchema>;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setFocus,
  } = useForm<FormData>({
    defaultValues: {
      date: date ? new Date(date) : undefined,
    },
    resolver: zodResolver(FormSchema),
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await Committees.updateCommittee(committeeID, {
        [fieldName as keyof db_GetCommitteeRow]: formatDateAsUTC(data.date as Date),
      });
      queryClient.invalidateQueries({ queryKey: ['committees'] });
      navigate(`/committees/${committeeID}/view`);
    } catch {
      setSubmitError(`Error updating ${label.toLowerCase()}.`);
    }

    setIsSubmitting(false);
  };

  const validationErrors = Object.keys(errors);

  return (
    <GridRow>
      <GridColumn width="three-quarters">
        <Breadcrumbs>
          <BreadcrumbItem to="/dashboard">Dashboard</BreadcrumbItem>
          <BreadcrumbItem to={`/committees/${committeeID}/view`}>{committeeData?.committee_name}</BreadcrumbItem>
          <BreadcrumbItem>Change {label.toLowerCase()}</BreadcrumbItem>
        </Breadcrumbs>

        {submitError && !validationErrors.length && <ErrorSummary description={submitError} />}

        <form onSubmit={handleSubmit(onSubmit)}>
          {!!validationErrors?.length && (
            <ErrorSummary
              errors={validationErrors.map((key) => ({
                targetName: key,
                text: errors[key as keyof typeof errors]?.message,
              }))}
              onHandleErrorClick={(targetName: string) => setFocus(targetName as keyof FormData)}
            />
          )}

          <Fieldset>
            <Legend isPageHeading size="l">
              Change the {label.toLowerCase()} for {committeeData?.committee_name}
            </Legend>

            <Controller
              control={control}
              name="date"
              render={({ field }) => <DateInput error={errors?.date?.message} includeTime {...field} />}
            />

            <Button type="submit" disabled={isSubmitting}>
              Continue
            </Button>
          </Fieldset>
        </form>
      </GridColumn>
    </GridRow>
  );
}

export default function CommitteeDatePage(props: CommitteeDateProps) {
  const params = useParams<Params>();
  const committeeID = parseInt(params.id ?? '0');

  const { data: committeeData, isLoading: committeeIsLoading } = useQuery(getCommitteeQuery(committeeID));

  if (committeeIsLoading) return <LoadingPage />;
  if (committeeData?.committee) return <CommitteeDateForm committeeData={committeeData.committee} {...props} />;
}
